<template>
  <div class="conv">
    <div class="conv__title">{{ $t("convAttendance.title") }}</div>
    <div class="conv__sub">
      {{ $t("convAttendance.sub") }}
    </div>

    <!-- Card 86aknajnn: o extrato cresce sem fim conforme o aluno participa.
         O seletor só oferece meses que têm registro. -->
    <div v-if="data.months.length > 1" class="conv__filter mt-4">
      <label class="conv__filter-k" for="conv-month">{{
        $t("convAttendance.period")
      }}</label>
      <select
        id="conv-month"
        class="conv__filter-select"
        :value="month"
        @change="changeMonth($event.target.value)"
      >
        <option v-for="option in data.months" :key="option" :value="option">
          {{ monthLabel(option) }}
        </option>
        <option value="">{{ $t("convAttendance.allMonths") }}</option>
      </select>
    </div>

    <page-skeleton v-if="loading" variant="rows" :rows="3" class="mt-4" />

    <template v-else-if="data.items.length">
      <!-- Conversação e monitoria contadas separadamente: uma falta em
           monitoria não diz a mesma coisa que uma falta em aula. -->
      <div class="conv__groups mt-4">
        <section
          v-for="group in typeGroups"
          :key="group.key"
          class="conv__group"
        >
          <div class="conv__group-title">{{ group.label }}</div>
          <div class="conv__stats">
            <div class="conv__stat">
              <span class="conv__stat-k">{{
                $t("convAttendance.present")
              }}</span>
              <span class="conv__stat-v">{{ group.summary.present }}</span>
            </div>
            <div class="conv__stat">
              <span class="conv__stat-k">{{
                $t("convAttendance.absent")
              }}</span>
              <span class="conv__stat-v">{{ group.summary.absent }}</span>
            </div>
            <div class="conv__stat">
              <span class="conv__stat-k">{{ $t("convAttendance.rate") }}</span>
              <span class="conv__stat-v">{{
                group.summary.attendanceRate === null
                  ? "—"
                  : `${group.summary.attendanceRate}%`
              }}</span>
            </div>
          </div>
        </section>
      </div>

      <div class="conv__list mt-5">
        <div
          v-for="item in data.items"
          :key="item.id"
          class="conv__row"
          :class="`conv__row--${attendanceTone(item.attendance)}`"
        >
          <div class="conv__row-main">
            <span class="conv__row-title">
              {{ item.title }}
              <!-- Aula em que o aluno apareceu sem ter confirmado e o professor
                   registrou a presença na chamada. -->
              <span v-if="item.addedByTeacher" class="conv__row-badge">
                {{ $t("convAttendance.byTeacher") }}
              </span>
            </span>
            <span class="conv__row-meta"
              >{{ item.typeLabel }} · {{ item.startsAtLabel }} ·
              {{ item.teacherName }}</span
            >
          </div>
          <span class="conv__row-tag">{{ attendanceText(item) }}</span>
        </div>
      </div>
    </template>

    <p v-else-if="month" class="conv__sub mt-4 mb-0">
      {{ $t("convAttendance.noneInPeriod") }}
    </p>

    <p v-else class="conv__sub mt-4 mb-0">
      {{ $t("convAttendance.none") }}
    </p>
  </div>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import { formatMonthKey } from "@/plugins/i18n";

// Card 86ajhjzyc: o extrato de conversação saiu do perfil e passou a viver
// junto das turmas para quem tem professor. O aluno Autonomy o vê na aba
// Conversação do perfil (card 86aknajnn). O componente carrega os próprios
// dados porque as duas telas que o usam não têm nada mais em comum.
const EMPTY_SUMMARY = () => ({
  confirmed: 0,
  present: 0,
  absent: 0,
  attendanceRate: null,
});

const EMPTY = () => ({
  items: [],
  months: [],
  month: null,
  summary: EMPTY_SUMMARY(),
  byType: { class: EMPTY_SUMMARY(), mentoring: EMPTY_SUMMARY() },
});

export default {
  name: "ConversationAttendance",

  data() {
    return {
      loading: true,
      // Card 86aknajnn: null até a primeira resposta dizer quais meses existem.
      month: null,
      data: EMPTY(),
    };
  },

  computed: {
    // A ordem é fixa: conversação primeiro, monitoria depois — é a leitura que
    // o card pede, e inverter conforme o volume de dados confundiria.
    typeGroups() {
      const byType = this.data.byType || {};
      return [
        {
          key: "class",
          label: this.$t("convAttendance.classes"),
          summary: byType.class || EMPTY_SUMMARY(),
        },
        {
          key: "mentoring",
          label: this.$t("convAttendance.mentoring"),
          summary: byType.mentoring || EMPTY_SUMMARY(),
        },
      ];
    },
  },

  created() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;

      // A promise é devolvida (aqui e no reload abaixo) para o `finally` só
      // apagar o "carregando" quando a segunda chamada também terminar.
      return customerApi
        .getGeneralClassAttendance(this.month)
        .then((res) => {
          this.data = { ...EMPTY(), ...res.data };

          // Primeira carga: abre no mês mais recente COM registro. Abrir no mês
          // atual mostraria tela vazia a quem tem histórico, e "todos" mantém a
          // rolagem infinita que o card quer evitar.
          if (this.month === null && this.data.months.length) {
            this.month = this.data.months[0];
            return this.load();
          }
        })
        .catch(() => {
          this.data = EMPTY();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeMonth(value) {
      this.month = value || "";
      this.load();
    },
    monthLabel(value) {
      return formatMonthKey(value, value);
    },
    // O rótulo vem pronto da API (em português); com o código da presença a
    // tela monta o texto no idioma escolhido.
    attendanceText(item) {
      if (+item.attendance === 1) return this.$t("enums.attendance.present");
      if (+item.attendance === 2) return this.$t("enums.attendance.absent");
      return item.attendanceLabel || this.$t("enums.attendance.pending");
    },
    // 1 = presente, 2 = falta; null = ainda não registrada pelo professor.
    attendanceTone(attendance) {
      if (+attendance === 1) return "present";
      if (+attendance === 2) return "absent";
      return "pending";
    },
  },
};
</script>

<style scoped>
.conv {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--dk-line, #ece7f2);
  border-radius: 18px;
  padding: 22px 24px;
}

.conv__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.conv__sub {
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6472);
}

.conv__filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.conv__filter-k {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dk-muted, #6b6472);
}

.conv__filter-select {
  border: 1px solid var(--dk-line, #ece7f2);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 0.85rem;
  color: var(--dk-ink, #231a2e);
  background: var(--dk-surface, #fff);
  cursor: pointer;
}

/* Os dois tipos lado a lado: a comparação entre eles é o ponto. */
.conv__groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.conv__group-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--dk-ink-soft, #4b4551);
  margin-bottom: 8px;
}

.conv__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.conv__stat {
  border: 1px solid var(--dk-line, #ece7f2);
  border-radius: 14px;
  padding: 12px 14px;
}

.conv__stat-k {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dk-muted, #6b6472);
}

.conv__stat-v {
  display: block;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--dk-ink, #231a2e);
  line-height: 1.2;
}

.conv__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-left: 3px solid var(--dk-line-strong, #cfc9d6);
  border-radius: 0 12px 12px 0;
  background: var(--dk-paper, #faf9fc);
  margin-bottom: 8px;
}

.conv__row--present {
  border-left-color: var(--dk-teal, #00a379);
}

.conv__row--absent {
  border-left-color: var(--dk-red, #d52323);
}

.conv__row-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.conv__row-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
}

.conv__row-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border: 1px solid var(--dk-line-strong, #cfc9d6);
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 500;
  color: var(--dk-muted, #6b6472);
}

.conv__row-meta {
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6472);
}

.conv__row-tag {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--dk-ink-soft, #4b4551);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .conv__groups {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .conv__stats {
    grid-template-columns: 1fr;
  }
}
</style>
